import { AlertCircle, ArrowLeftRight, CalendarClock, Check, CircleCheck, CreditCard, PauseCircle } from 'lucide-react';
import { billedTileLabel, formatMoney, intervalSuffix, renewalDate, HOUSEHOLD_LABELS } from './helper';

const Tile = ({ label, value }) => (
  <div className="rounded-xl border border-success-100 bg-white px-4 py-3 text-center">
    <p className="text-[10px] font-semibold uppercase tracking-widest text-secondary-400">{label}</p>
    <p className="mt-1 text-sm font-bold text-secondary-900">{value}</p>
  </div>
);

const MemberPanel = ({
  enrollment,
  discountPercent = null,
  justJoined = false,
  busy = false,
  onCancel = null,
  onFixPayment = null,
  onChangePlan = null,
  onKeepPlan = null,
  insured = false,
}) => {
  const {
    plan_name, household_type, interval, price_dollars, state, members = [],
    pending_plan_name, pending_household_type, pending_price_dollars, cancellation_requested,
    // Collection is paused at the practice's end. Still covered, just not billed.
    billing_paused = false,
    // A covered dependant sees the plan they're on but owns none of it — the
    // API refuses their cancel/resume calls, so the buttons never appear.
    manageable = true, subscriber_name,
  } = enrollment || {};

  // A scheduled cancellation is MembershipEndingNotice's job — this panel only
  // ever renders a membership that is simply running.
  const renewal = renewalDate(enrollment);

  return (
    <div className="animate-slide-up rounded-2xl border border-success-100 bg-success-50/40 p-6 sm:p-8">
      <div className="flex flex-col items-center text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success-500 text-white shadow-lg shadow-success-200/50">
          <Check size={26} strokeWidth={3} />
        </span>

        <h3 className="mt-4 text-xl font-bold text-secondary-900">
          {justJoined ? "You're a member!" : "You're a member"}
        </h3>

        {/* Claiming insurance isn't needed would contradict the card sitting
            right above this once they have one on file. */}
        <p className="mt-1.5 max-w-md text-xs leading-relaxed text-secondary-500">
          {insured
            ? `Your ${plan_name} membership runs alongside your insurance, with member pricing on treatment your plan doesn't cover.`
            : `No dental insurance needed — your ${plan_name} membership covers you instead, with member pricing on treatment ${justJoined ? 'from today' : 'while it stays active'}.`}
        </p>

        <div className="mt-5 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
          <Tile label="Plan" value={`${plan_name} · ${HOUSEHOLD_LABELS[household_type] || household_type}`} />
          <Tile label={billedTileLabel(interval)} value={`${formatMoney(price_dollars)} ${intervalSuffix(interval)}`} />
          {renewal && !billing_paused && <Tile label="Next billing" value={renewal} />}
          {billing_paused && <Tile label="Next billing" value="On hold" />}
        </div>

        {/* Only worth listing when the plan covers more than the subscriber */}
        {members.length > 1 && (
          <div className="mt-5 w-full max-w-2xl rounded-xl border border-success-100 bg-white px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-secondary-400">
              Who's covered
            </p>
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {members.map(({ id, name }) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1 text-xs font-semibold text-secondary-700"
                >
                  <Check size={12} strokeWidth={3} className="text-success-500" />
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* null means no discount is configured, which is not the same as 0% */}
        {discountPercent != null && (
          <span className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-success-100 bg-success-50 px-3.5 py-1.5 text-xs font-semibold text-success-700">
            <CircleCheck size={13} strokeWidth={2.5} />
            Your {discountPercent}% member discount is active
          </span>
        )}

        {/* Sent to the practice, not actioned yet. Nothing has changed, so the
            panel stays green and simply says what is being waited on. */}
        {cancellation_requested && (
          <div className="mt-5 w-full max-w-2xl rounded-xl border border-amber-100 bg-amber-50 px-4 py-3.5 text-left">
            <p className="flex items-start gap-2 text-xs font-medium leading-relaxed text-amber-800">
              <CalendarClock size={14} strokeWidth={2.5} className="mt-0.5 shrink-0" />
              <span>
                Your cancellation request is with the practice. You're still covered and your
                benefits are unchanged — they'll be in touch to confirm whether your cover runs to
                {renewal ? ` ${renewal}` : ' the end of this period'} or ends sooner with a refund.
              </span>
            </p>
          </div>
        )}

        {/* A downgrade Stripe already bills for from the renewal — the patient
            keeps this tier until then, so say so rather than showing the new
            plan as if it were live. */}
        {pending_plan_name && (
          <div className="mt-5 w-full max-w-2xl rounded-xl border border-secondary-200 bg-white px-4 py-3.5 text-left">
            <p className="flex items-start gap-2 text-xs font-medium leading-relaxed text-secondary-600">
              <CalendarClock size={14} strokeWidth={2.5} className="mt-0.5 shrink-0 text-secondary-400" />
              <span>
                You're moving to{' '}
                <strong>
                  {pending_plan_name}
                  {pending_household_type && ` · ${HOUSEHOLD_LABELS[pending_household_type] || pending_household_type}`}
                </strong>
                {pending_price_dollars != null && (
                  <> at {formatMoney(pending_price_dollars)} {intervalSuffix(interval)}</>
                )}{' '}
                on <strong>{renewal}</strong>. Until then everyone on your {plan_name} plan stays
                covered — nothing has been charged for the change.
              </span>
            </p>

            {onKeepPlan && manageable && (
              <button
                type="button"
                onClick={onKeepPlan}
                disabled={busy}
                className="btn mt-3 h-9 min-h-9 gap-2 rounded-xl border border-secondary-200 bg-white px-3 text-xs font-semibold text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
              >
                {busy ? 'Updating…' : `Keep ${plan_name} instead`}
              </button>
            )}
          </div>
        )}

        {billing_paused && (
          <div className="mt-5 w-full max-w-2xl rounded-xl border border-secondary-200 bg-secondary-50 px-4 py-3.5 text-left">
            <p className="flex items-start gap-2 text-xs font-medium leading-relaxed text-secondary-700">
              <PauseCircle size={14} strokeWidth={2.5} className="mt-0.5 shrink-0" />
              <span>
                <strong>Billing is on hold.</strong> You're still a member and your plan still
                covers you, but nothing is being charged at the moment because your patient
                record isn't active at the practice. Give them a call and they'll switch it
                back on — nothing has been cancelled. You can't switch plans until they do.
              </span>
            </p>
          </div>
        )}

        {state === 'past_due' && !billing_paused && (
          <div className="mt-5 w-full max-w-2xl rounded-xl border border-amber-100 bg-amber-50 px-4 py-3.5 text-left">
            <p className="flex items-start gap-2 text-xs font-medium leading-relaxed text-amber-800">
              <AlertCircle size={14} strokeWidth={2.5} className="mt-0.5 shrink-0" />
              <span>
                Your last renewal payment didn't go through — usually an expired or replaced
                card. You're still covered while we retry, but the membership will end if we
                can't collect.
              </span>
            </p>

            {onFixPayment && manageable && (
              <button
                type="button"
                onClick={onFixPayment}
                disabled={busy}
                className="btn mt-3 h-10 min-h-10 w-full gap-2 rounded-xl border-0 bg-amber-600 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50 sm:w-auto"
              >
                <CreditCard size={14} strokeWidth={2.5} />
                {busy ? 'Loading…' : 'Update payment method'}
              </button>
            )}
          </div>
        )}

        {manageable ? (
          <div className="mt-5 flex flex-col items-center gap-3">
            {/* Hidden while a change is already scheduled — the API refuses a
                second one until this one is undone. */}
            {onChangePlan && !pending_plan_name && !cancellation_requested && !billing_paused && (
              <button
                type="button"
                onClick={onChangePlan}
                disabled={busy}
                className="btn h-10 min-h-10 gap-2 rounded-xl border border-secondary-200 bg-white px-4 text-xs font-semibold text-secondary-900 hover:bg-secondary-50 disabled:opacity-50"
              >
                <ArrowLeftRight size={14} strokeWidth={2.5} />
                Change plan
              </button>
            )}

            {onCancel && !cancellation_requested && (
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="text-xs font-semibold text-secondary-400 underline underline-offset-2 hover:text-secondary-600 disabled:opacity-50"
              >
                Cancel membership
              </button>
            )}
          </div>
        ) : (
          <p className="mt-5 text-xs text-secondary-400">
            {subscriber_name || 'The subscriber'} pays for this membership and manages any changes.
          </p>
        )}
      </div>
    </div>
  );
};

export default MemberPanel;
